import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  AFTER_2017_ADDITIONAL_COLUMNS,
  AFTER_2017_HEADER,
} from '../../../../core/constants/issues-2017-2018.constants';
import {
  After2017ApiResponseDTO,
  After2017HeaderLabels,
  After2017RecordDTO,
  After2017Row,
} from '../../../../core/interface/issues-2017-2018.interface';
import { After2017Header } from './after-2017-header/after-2017-header';
import {
  FooterSummaryRow,
  IncomeAfter2017Total,
} from '../../../../core/interface/all-issues.interface';
import { AuditTrailRecordDTO } from '../../../../core/interface/audit-trail.interface';
import {
  AUDIT_TRAIL_CONSTANT,
  AUDIT_TRAIL_TABLE_COLUMNS,
} from '../../../../core/constants/auditTrail.constants';
import { AppCommonService } from '../../../../core/services/app-common.service';
import { AppModalService } from '../../../../core/services/app-modal.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AppIssuesService } from '../../../../core/services/issues.service';
import { NgClass } from '@angular/common';
import { ToastService } from '../../../../core/services/app-toast.service';

@Component({
  selector: 'app-issue-after-2017',
  standalone: true,
  imports: [After2017Header, ReactiveFormsModule, FormsModule, NgClass],
  templateUrl: './issue-after-2017.html',
  styleUrl: './issue-after-2017.scss',
})
export class IssueAfter2017 implements OnChanges {
  @Input()
  screen: 'existing' | 'allIssues' = 'existing';
  @Input()
  action: 'view' | 'edit' = 'view';
  @Input() incomeAfter2017Total!: IncomeAfter2017Total;
  @Input() auditChangesAfter2017Total!: IncomeAfter2017Total;
  @Input({ required: true }) records!: After2017RecordDTO[];
  @Input() showHideIcons: boolean = false;
  @Input() isExistingHeaderVisible: boolean = false;
  @Output() openLink = new EventEmitter<string>();
  @Output() editRecordEvent = new EventEmitter<number>();
  @Output() deleteClicked = new EventEmitter<After2017RecordDTO>();
  @Output() gbcChanged = new EventEmitter<{
    recordId: number;
    value: string;
  }>();
  footerSummaryData: FooterSummaryRow[] = [];
  recordsData!: After2017ApiResponseDTO;

  readonly footerGridTemplate = `
  minmax(80px, 0.8fr)   /* IssueId */
  minmax(65px, 0.7fr)   /* Label */
  minmax(80px, 0.9fr)   /* US */
  minmax(150px, 1.5fr)  /* Branch NOGI */
  minmax(150px, 1.5fr)  /* Branch CFOGI */
  minmax(80px, 0.9fr)   /* NBr NOGI */
  minmax(150px, 1.5fr)  /* NBr CFOGI */
  minmax(90px, 1fr)     /* Passive */
  minmax(95px, 1fr)     /* GILTI NOGI */
  minmax(90px, 1fr)     /* GILTI FORI */
  minmax(90px, 1fr)     /* Capital Gain */
  minmax(115px, 1fr)    /* TOTAL */
`;

  readonly existingTemplate = `
  minmax(100px, 0.5fr)
  minmax(120px, 1fr)
  minmax(250px, 2fr)
  minmax(250px, 2fr)
  minmax(120px, 1fr)
  minmax(250px, 2fr)
  minmax(120px, 1fr)
`;

  readonly allIssuesTemplate = `
  minmax(80px, 0.8fr)
  minmax(65px, 0.7fr)
  minmax(80px, 0.9fr)
  minmax(150px, 1.5fr)
  minmax(150px, 1.5fr)
  minmax(80px, 0.9fr)
  minmax(150px, 1.5fr)
  minmax(90px, 1fr)
  minmax(95px, 1fr)
  minmax(90px, 1fr)
  minmax(90px, 1fr)
`;

  readonly columns = [
    'us',
    'branchNogi',
    'branchCfogi',
    'nonBranchNogi',
    'nonBranchCfogi',
    'passive',
    'giltiNogi',
    'giltiFori',
    'capitalGain',
  ] as const;

  private readonly appModalService = inject(AppModalService);
  private readonly commonService = inject(AppCommonService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly issuesService = inject(AppIssuesService);
  private readonly toastService = inject(ToastService);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['incomeAfter2017Total']?.currentValue) {
      this.bindIncomeAfter2017Total(changes['incomeAfter2017Total'].currentValue, false);
    }

    if (changes['auditChangesAfter2017Total']?.currentValue) {
      this.bindIncomeAfter2017Total(changes['auditChangesAfter2017Total'].currentValue, true);
    }
  }

  createEmptyAfter2017Record(): After2017RecordDTO {
    return {
      id: 0,
      issueId: '',
      year: 0,
      entity: '',
      cfc: '',
      isCancelled: false,
      isFilled: false,

      rows: [
        {
          label: 'TI b/f 199',
          us: '',
          branchNogi: '',
          branchCfogi: '',
          nonBranchNogi: '',
          nonBranchCfogi: '',
          passive: '',
          giltiNogi: '',
          npaLast: false,
          giltiFori: '',
          capitalGain: '',
        },
        {
          label: '',
          us: '',
          branchNogi: '',
          branchCfogi: '',
          nonBranchNogi: '',
          nonBranchCfogi: '',
          passive: '',
          giltiNogi: '',
          npaLast: false,
          giltiFori: '',
          capitalGain: '',
        },
        {
          label: 'FTC',
          us: '',
          branchNogi: '',
          branchCfogi: '',
          nonBranchNogi: '',
          nonBranchCfogi: '',
          passive: '',
          giltiNogi: '',
          npaLast: false,
          giltiFori: '',
          capitalGain: '',
        },
        {
          label: 'GBC',
          us: '',
          branchNogi: '',
          branchCfogi: '',
          nonBranchNogi: '',
          nonBranchCfogi: '',
          passive: '',
          giltiNogi: '',
          npaLast: false,
          giltiFori: '',
          capitalGain: '',
        },
        {
          label: '',
          us: '',
          branchNogi: '',
          branchCfogi: '',
          nonBranchNogi: '',
          nonBranchCfogi: '',
          passive: '',
          giltiNogi: '',
          npaLast: true,
          giltiFori: 'NPA ties AR/SAA',
          capitalGain: '',
        },
      ],

      arFilingId: null,
      arIssueId: null,
      irsIssueID: null,
      status: null,
      saaID: null,
      saaSubmission: null,
      docID: null,
      npa: null,
      saaChecked: false,
      epAdjText: 'ASC 740',
      isEPAdjRequired: null,
      isTBBSAdjRequired: null,
      dateCorptaxAdj: null,
    };
  }

  bindIncomeAfter2017Total(recordData: IncomeAfter2017Total, isAudit = false): void {
    const gbcValue = isAudit
      ? (recordData.audit_GBC_After_2017 ?? recordData.total_Audit_GBC_After_2017 ?? 0)
      : (recordData.gbC_After_2017 ?? recordData.gbc);
    const totalGbcValue = isAudit
      ? (recordData.total_Audit_GBC_After_2017 ?? recordData.audit_GBC_After_2017 ?? 0)
      : (recordData.total_GBC_After_2017 ?? recordData.total_GBC);

    this.footerSummaryData = [
      {
        label: 'TI b/f 199',
        values: [
          recordData.total_TI_US_2020,
          recordData.total_TI_Br_NOGI,
          recordData.total_TI_Br_CFOGI,
          recordData.total_TI_Nbr_NOGI,
          recordData.total_TI_NBr_CFOGI,
          recordData.total_TI_Passive_2020,
          recordData.total_TI_GILTI_NOGI,
          recordData.total_TI_GILTI_FORI,
          recordData.total_TI_CapitalGain_2020,
          recordData.total_TI,
        ],
      },
      {
        label: 'FTC',
        values: [
          null,
          recordData.total_FTC_Br_NOGI,
          recordData.total_FTC_Br_CFOGI,
          recordData.total_FTC_Nbr_NOGI,
          recordData.total_FTC_NBr_CFOGI,
          recordData.total_FTC_Passive_2020,
          recordData.total_FTC_GILTI_NOGI,
          recordData.total_FTC_GILTI_FORI,
          null,
          recordData.total_FTC,
        ],
      },
      {
        label: 'GBC',
        values: [gbcValue, null, null, null, null, null, null, null, null, totalGbcValue],
      },
    ];
  }

  get after2017TemplateHeaders(): string {
    return this.baseGridTemplate;
  }

  get baseGridTemplate(): string {
    return this.screen === 'allIssues' ? `${this.allIssuesTemplate} 40px` : this.existingTemplate;
  }

  get after2017TemplateRecords(): string {
    return this.screen === 'allIssues' ? this.allIssuesTemplate : this.existingTemplate;
  }

  get showAdditionalColumns(): boolean {
    return this.screen === 'allIssues';
  }

  get displayColumns(): After2017HeaderLabels[] {
    return this.showAdditionalColumns
      ? [AFTER_2017_HEADER[0], ...AFTER_2017_HEADER.slice(1), ...AFTER_2017_ADDITIONAL_COLUMNS]
      : AFTER_2017_HEADER.slice(1);
  }

  shouldDisplayRow(rowIndex: number, totalRows: number): boolean {
    if (this.screen === 'allIssues') {
      return true;
    }
    return rowIndex !== 1 && rowIndex !== totalRows - 1;
  }

  showAudit(record: any): void {
    this.commonService
      .getAuditTrailById(record.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: AuditTrailRecordDTO[]) => {
        const columns = AUDIT_TRAIL_TABLE_COLUMNS;
        this.appModalService.openAudit(AUDIT_TRAIL_CONSTANT.auditTrail, columns, data);
      });
  }

  editRecord(row: any) {
    if (row.isFilled) {
      const msg = `This AR is filed, you are not allowed to update the adjustment.`;
      this.toastService.error(msg);
    } else {
      this.appModalService.updateEditIssueStatus(true);
      this.editRecordEvent.emit(Number(row.id));
    }
  }

   /** ISSUE_DELETE_CANCEL */
    deleteRecord(row: After2017RecordDTO): void {
      if (!this.commonService.canIssueBeCancelled()) {
        return;
      }
      if (row.isFilled) {
        const msg = `This AR is filed, you are not allowed to cancel the adjustment.`;
        this.toastService.error(msg);
      } else {
        this.deleteClicked.emit(row);
      }
    }

  onGbcUsChanged(row: any, record: After2017RecordDTO, value: string): void {
    this.updateCellValue(row, 'us', value, record);
  }

  updateCellValue(
    row: After2017Row,
    key: keyof After2017Row,
    value: string,
    record?: After2017RecordDTO,
  ): void {
    const nextValue = String(value ?? '');
    if (!this.isIntegerValue(nextValue)) {
      return;
    }

    row[key] = nextValue as never;

    if (record && key === 'us' && row.label === 'GBC') {
      this.gbcChanged.emit({
        recordId: record.id ?? 0,
        value: nextValue,
      });
    }
  }

  allowIntegerInput(event: KeyboardEvent, currentValue: string): void {
    const key = event.key;
    if (['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(key)) {
      return;
    }

    if (/^\d$/.test(key)) {
      return;
    }

    if (key === '-') {
      const input = event.target as HTMLInputElement | null;
      const selectionStart = input?.selectionStart ?? currentValue.length;
      const selectionEnd = input?.selectionEnd ?? currentValue.length;
      const nextValue = this.buildNextValue(currentValue, key, selectionStart, selectionEnd);
      if (nextValue === '-' || /^-?\d*$/.test(nextValue)) {
        if (selectionStart === 0 && !nextValue.slice(1).includes('-')) {
          return;
        }
      }
      event.preventDefault();
      return;
    }

    event.preventDefault();
  }

  handleIntegerPaste(
    event: ClipboardEvent,
    row: After2017Row,
    key: keyof After2017Row,
    record?: After2017RecordDTO,
  ): void {
    const pasteText = event.clipboardData?.getData('text') ?? '';
    const input = event.target as HTMLInputElement | null;
    const currentValue = String(row[key] ?? '');
    const selectionStart = input?.selectionStart ?? currentValue.length;
    const selectionEnd = input?.selectionEnd ?? currentValue.length;
    const nextValue = this.buildNextValue(currentValue, pasteText, selectionStart, selectionEnd);

    if (!this.isIntegerValue(nextValue)) {
      event.preventDefault();
      return;
    }

    event.preventDefault();
    this.updateCellValue(row, key, nextValue, record);
  }

  handleIntegerDrop(
    event: DragEvent,
    row: After2017Row,
    key: keyof After2017Row,
    record?: After2017RecordDTO,
  ): void {
    const dropText = event.dataTransfer?.getData('text') ?? '';
    const input = event.target as HTMLInputElement | null;
    const currentValue = String(row[key] ?? '');
    const selectionStart = input?.selectionStart ?? currentValue.length;
    const selectionEnd = input?.selectionEnd ?? currentValue.length;
    const nextValue = this.buildNextValue(currentValue, dropText, selectionStart, selectionEnd);

    if (!this.isIntegerValue(nextValue)) {
      event.preventDefault();
      return;
    }

    event.preventDefault();
    this.updateCellValue(row, key, nextValue, record);
  }

  private isIntegerValue(value: string): boolean {
    return /^-?\d*$/.test(value);
  }

  private buildNextValue(
    currentValue: string,
    insertedText: string,
    selectionStart: number,
    selectionEnd: number,
  ): string {
    return `${currentValue.slice(0, selectionStart)}${insertedText}${currentValue.slice(selectionEnd)}`;
  }

  getSpDocLink(value: string | null | undefined) {
    return this.commonService.getSpDocLink(value);
  }

  navigateToLink(value: string): void {
    const spDocLink = this.getSpDocLink(value);
    if (spDocLink.url) {
      this.openLink.emit(spDocLink.url);
    } else if (!spDocLink.hasSeparator && spDocLink.displayText) {
      this.toastService.error('Invalid Doc Link');
    }
  }

  onLinkKeydown(event: KeyboardEvent, value: string): void {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    event.preventDefault();
    this.navigateToLink(value);
  }
}
